import React from 'react';
import {
  DataGrid,
  GridColDef,
  GridToolbar,
  GridRowParams,
  GridActionsCellItem,
  GridActionsCellItemProps,
  GridActionsColDef,
  GridRowSelectionModel,
  GridPaginationModel,
  GridSortModel,
  GridFilterModel,
} from '@mui/x-data-grid';
import { Box, Paper, type SxProps, type Theme } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

interface DataTableProps {
  rows: any[];
  columns: GridColDef[];
  loading?: boolean;
  onEditClick?: (id: string) => void;
  onDeleteClick?: (id: string) => void;
  hideToolbar?: boolean;
  customActions?: (params: GridRowParams) => React.ReactElement<GridActionsCellItemProps>[];
  getRowId?: (row: any) => string;
  checkboxSelection?: boolean;
  onSelectionModelChange?: (selection: GridRowSelectionModel) => void;
  selectionModel?: GridRowSelectionModel;
  pageSizeOptions?: number[];
  defaultPageSize?: number;
  fullHeight?: boolean;
  /** Height offset in pixels when fullHeight is true (default: 250) */
  heightOffset?: number;
  /** When true, table height fits its content up to maxHeight */
  autoHeight?: boolean;
  /** Max height in pixels when autoHeight is true (default: 600) */
  maxHeight?: number;
  /** Custom row height function — pass () => 'auto' for variable-height rows */
  getRowHeight?: () => 'auto' | number;
  /** Additional sx props passed to the DataGrid */
  dataGridSx?: SxProps<Theme>;
  // Server-side pagination/sorting (optional — when absent, client-side behavior)
  paginationMode?: 'server' | 'client';
  sortingMode?: 'server' | 'client';
  rowCount?: number;
  paginationModel?: GridPaginationModel;
  onPaginationModelChange?: (model: GridPaginationModel) => void;
  sortModel?: GridSortModel;
  onSortModelChange?: (model: GridSortModel) => void;
  filterModel?: GridFilterModel;
  onFilterModelChange?: (model: GridFilterModel) => void;
}

export const DataTable: React.FC<DataTableProps> = ({
  rows,
  columns,
  loading = false,
  onEditClick,
  onDeleteClick,
  hideToolbar = false,
  customActions,
  getRowId,
  checkboxSelection = false,
  onSelectionModelChange,
  selectionModel,
  pageSizeOptions = [10, 25, 50],
  defaultPageSize = 25,
  fullHeight = false,
  heightOffset = 250,
  autoHeight = false,
  maxHeight = 600,
  getRowHeight,
  dataGridSx,
  paginationMode,
  sortingMode,
  rowCount,
  paginationModel,
  onPaginationModelChange,
  sortModel,
  onSortModelChange,
  filterModel,
  onFilterModelChange,
}) => {
  const finalColumns = React.useMemo(() => {
    // Strip resizable prop — it's Pro-only and triggers a console warning
    const sanitizedColumns = columns.map(({ resizable, ...col }) => col);

    if (!onEditClick && !onDeleteClick && !customActions) {
      return sanitizedColumns;
    }

    const actionsColumn: GridActionsColDef = {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 100,
      getActions: (params: GridRowParams) => {
        const actions: React.ReactElement<GridActionsCellItemProps>[] = [];

        if (customActions) {
          actions.push(...customActions(params));
        }

        if (onEditClick) {
          actions.push(
            <GridActionsCellItem
              key="edit"
              icon={<EditIcon />}
              label="Edit"
              onClick={() => onEditClick(params.id as string)}
              showInMenu={false}
            />
          );
        }

        if (onDeleteClick) {
          actions.push(
            <GridActionsCellItem
              key="delete"
              icon={<DeleteIcon />}
              label="Delete"
              onClick={() => onDeleteClick(params.id as string)}
              showInMenu={false}
            />
          );
        }

        return actions;
      },
    };

    return [...sanitizedColumns, actionsColumn];
  }, [columns, onEditClick, onDeleteClick, customActions]);

  const isServerMode = paginationMode === 'server';

  return (
    <Box sx={{ width: '100%', height: autoHeight ? maxHeight : fullHeight ? `calc(100vh - ${heightOffset}px)` : 600, minHeight: autoHeight ? undefined : 400 }}>
      <Paper elevation={2} sx={{ height: '100%' }}>
        <DataGrid
          rows={rows}
          columns={finalColumns}
          loading={loading}
          disableRowSelectionOnClick
          checkboxSelection={checkboxSelection}
          keepNonExistentRowsSelected={isServerMode && checkboxSelection}
          onRowSelectionModelChange={(newSelection: GridRowSelectionModel) => {
            if (onSelectionModelChange) {
              onSelectionModelChange(newSelection);
            }
          }}
          rowSelectionModel={selectionModel}
          slots={hideToolbar ? {} : { toolbar: GridToolbar }}
          // In server mode, column filters are wired to the backend via filterMode="server".
          // The usePaginatedQuery hook manages filterModel state and sends filter_field/filter_op/filter_value
          // query params to the backend's Paginatable concern.
          filterMode={isServerMode ? 'server' : 'client'}
          filterModel={isServerMode ? filterModel : undefined}
          onFilterModelChange={isServerMode ? onFilterModelChange : undefined}
          // Pagination
          paginationMode={paginationMode ?? 'client'}
          sortingMode={sortingMode ?? 'client'}
          rowCount={isServerMode ? rowCount : undefined}
          paginationModel={isServerMode ? paginationModel : undefined}
          onPaginationModelChange={isServerMode ? onPaginationModelChange : undefined}
          sortModel={isServerMode ? sortModel : undefined}
          onSortModelChange={isServerMode ? onSortModelChange : undefined}
          initialState={
            isServerMode
              ? undefined
              : { pagination: { paginationModel: { pageSize: defaultPageSize } } }
          }
          pageSizeOptions={pageSizeOptions}
          getRowId={getRowId}
          getRowHeight={getRowHeight}
          sx={dataGridSx}
        />
      </Paper>
    </Box>
  );
};
